import { DateTime } from 'luxon';

import ContextMenu from 'dashboard/components/ContextMenu';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import Pagination from 'dashboard/components/Pagination';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import TableLayout, {
  TableActionTD,
  TableLinkTR,
  TableNullState,
  TableTD,
  TableTH,
} from 'dashboard/layouts/TableLayout';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import {
  CaseAssignmentPolicy,
  DISTRIBUTION_TYPE_LABELS,
} from 'dashboard/models/case-assignment-policy';
import { Environment } from 'dashboard/models/environment';
import FormCreateCaseAssignmentPolicy, {
  CreateCaseAssignmentPolicyFormData,
} from 'dashboard/views/DashboardCaseAssignmentPoliciesView/FormCreateCaseAssignmentPolicy';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import { HStack } from 'design-system/components/Stack';
import Tooltip from 'design-system/components/Tooltip';
import { DATE_AND_TIME_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import useConfirmation from 'lib/hooks/useConfirmation';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { PaginationParams } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';
import { permissionErrorString } from 'lib/permissions-errors';
import { generateEnhancedMaxLimitReachedMessage } from 'lib/product-feature-errors';

const CASES_FREE_ASSIGNMENT_POLICY_LIMIT = 1;
const CASES_BASIC_ASSIGNMENT_POLICY_LIMIT = 3;

export interface DashboardCaseAssignmentPoliciesViewProps {
  atActiveCaseAssignmentPolicyLimit: boolean;
  currentEnvironment: Environment;
  hasCasesFree: boolean;
  hasCasesBasic: boolean;
  loadingCaseAssignmentPolicies: boolean;
  caseAssignmentPolicies: CaseAssignmentPolicy[];
  pageSize: number | undefined;
  paginationParams: PaginationParams | undefined;
  sortParams: SortParams | undefined;
  createCaseAssignmentPolicy: (values: CreateCaseAssignmentPolicyFormData) => void;
  archiveCaseAssignmentPolicy: (caseAssignmentPolicy: CaseAssignmentPolicy) => void;
  handlePageLinkClick: (pageNumber: number) => void;
  handlePageSizeClick: (pageSize: number) => void;
  handleSortUpdate: (sortParams: SortParams) => void;
}

const DashboardCaseAssignmentPoliciesView = ({
  atActiveCaseAssignmentPolicyLimit,
  caseAssignmentPolicies,
  currentEnvironment,
  hasCasesFree,
  hasCasesBasic,
  loadingCaseAssignmentPolicies,
  pageSize,
  paginationParams,
  sortParams,
  createCaseAssignmentPolicy,
  archiveCaseAssignmentPolicy,
  handlePageLinkClick,
  handlePageSizeClick,
  handleSortUpdate,
}: DashboardCaseAssignmentPoliciesViewProps) => {
  const [
    showCreateAssignmentPolicyModal,
    { enable: openCreateAssignmentPolicyModal, disable: hideCreateAssignmentPolicyModal },
  ] = useToggle();
  const [showArchiveCaseAssignmentPolicyModal, { prompt, confirm, cancel }] = useConfirmation();

  const handleCreateAssignmentPolicyAndClose = async (
    values: CreateCaseAssignmentPolicyFormData,
  ) => {
    await createCaseAssignmentPolicy(values);
    hideCreateAssignmentPolicyModal();
  };

  const environmentName = currentEnvironment?.attributes?.name;

  const hasAssignmentPolicyWritePermission = usePermission(Permission.CaseAssignmentPolicyWrite);
  const assignmentPolicyLimit = hasCasesBasic
    ? CASES_BASIC_ASSIGNMENT_POLICY_LIMIT
    : hasCasesFree
      ? CASES_FREE_ASSIGNMENT_POLICY_LIMIT
      : Infinity;

  const handleColumnHeaderClick = (columnName: string) => {
    handleSortUpdate({
      sort: columnName,
      shouldSortDescending:
        columnName === sortParams?.sort ? !sortParams.shouldSortDescending : true,
    });
  };

  const apRows = caseAssignmentPolicies.map((ap) => {
    const {
      name,
      status,
      'distribution-type': distributionType,
      'updated-at': updatedAt,
    } = ap.attributes;
    const tableRowLink = `/dashboard/case-assignment-policies/${ap.id}`;
    const updatedAtDisplay = updatedAt
      ? DateTime.fromISO(updatedAt).toFormat(DATE_AND_TIME_FORMAT)
      : '–';

    const handleArchiveCaseAssignmentPolicyClick = () =>
      prompt(() => archiveCaseAssignmentPolicy(ap));

    return (
      <TableLinkTR
        to={tableRowLink}
        id={`case-assignment-policies-table__row--${ap.id}`}
        key={ap.id}
      >
        <TableTD>{name}</TableTD>
        <TableTD>{status}</TableTD>
        <TableTD>{DISTRIBUTION_TYPE_LABELS[distributionType]}</TableTD>
        <TableTD>{updatedAtDisplay}</TableTD>
        <TableActionTD>
          <ContextMenu
            items={[
              {
                id: 'archive',
                onClick: handleArchiveCaseAssignmentPolicyClick,
                hidden: status !== 'active' || !hasAssignmentPolicyWritePermission,
                variant: 'danger',
                label: 'Archive',
              },
            ]}
            renderItem={(item) => (
              <ContextMenu.Item
                id={`case-assignment-policies-table__row-action-column--${ap.id}__${item.id}`}
                key={`case-assignment-policies-table__row-action-column--${ap.id}__${item.id}`}
                onClick={(e) => {
                  // Needed to avoid triggering row click below
                  e.stopPropagation();
                  e.preventDefault();
                  item.onClick?.();
                }}
                variant={item.variant}
              >
                {item.label}
              </ContextMenu.Item>
            )}
          />
        </TableActionTD>
      </TableLinkTR>
    );
  });

  return (
    <>
      <Container>
        <Header title='Assignment Policies'>
          <HStack>
            <Tooltip
              disabled={hasAssignmentPolicyWritePermission && !atActiveCaseAssignmentPolicyLimit}
              trigger={
                <Button
                  id={`case-assignment-policies-view__create-assignment-policy-button--${environmentName}`}
                  name='create-case-assignment-policy.button'
                  variant='primary'
                  onClick={openCreateAssignmentPolicyModal}
                  disabled={
                    !hasAssignmentPolicyWritePermission || atActiveCaseAssignmentPolicyLimit
                  }
                >
                  Create policy
                </Button>
              }
            >
              {!hasAssignmentPolicyWritePermission
                ? permissionErrorString(Permission.CaseAssignmentPolicyWrite)
                : generateEnhancedMaxLimitReachedMessage(
                    'assignment policies',
                    assignmentPolicyLimit,
                  )}
            </Tooltip>
          </HStack>
        </Header>
        <Section>
          <Card>
            <Card.Body>
              <Card.Title>Assignment Policies</Card.Title>
              <TableLayout
                isLoading={loadingCaseAssignmentPolicies}
                showHeader={true}
                isEmpty={caseAssignmentPolicies.length === 0}
                nullState={
                  <TableNullState hasUsedFilters={false}>
                    <h3>Looks like there aren&#39;t any assignment policies yet.</h3>
                    <p>Create an assignment policy to get started.</p>
                  </TableNullState>
                }
                headerTR={
                  <>
                    <TableTH
                      id='case-assignment-policies-table__name-header'
                      onClick={() => handleColumnHeaderClick('name')}
                      isSorted={sortParams?.sort === 'name'}
                      isDescending={sortParams?.shouldSortDescending}
                      canSort
                    >
                      Name
                    </TableTH>
                    <TableTH>Status</TableTH>
                    <TableTH>Distribution type</TableTH>
                    <TableTH
                      id='case-assignment-policies-table__updated-at-header'
                      onClick={() => handleColumnHeaderClick('updated_at')}
                      isSorted={sortParams?.sort === 'updated_at'}
                      isDescending={sortParams?.shouldSortDescending}
                      canSort
                    >
                      Last updated at ({DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})
                    </TableTH>
                    <TableTH>{/* Actions column */}</TableTH>
                  </>
                }
              >
                {apRows}
              </TableLayout>
              <Pagination
                pageSize={pageSize}
                paginationParams={paginationParams}
                handlePageSizeClick={handlePageSizeClick}
                handlePageLinkClick={handlePageLinkClick}
              />
            </Card.Body>
          </Card>
        </Section>
      </Container>
      <ModalWrapper
        isOpen={showCreateAssignmentPolicyModal}
        handleCloseModal={hideCreateAssignmentPolicyModal}
      >
        <ModalContainer>
          <ModalHeader onClose={hideCreateAssignmentPolicyModal}>
            Create a new assignment policy
          </ModalHeader>
          <ModalBody>
            <FormCreateCaseAssignmentPolicy
              defaultValues={{
                distributionType: 'round_robin',
                assignmentOrderBy: 'created_at',
              }}
              submit={handleCreateAssignmentPolicyAndClose}
              handleCancel={hideCreateAssignmentPolicyModal}
            />
          </ModalBody>
        </ModalContainer>
      </ModalWrapper>
      <ModalWrapper isOpen={showArchiveCaseAssignmentPolicyModal} handleCloseModal={cancel}>
        <ConfirmationModal
          cancelAction={cancel}
          confirmAction={confirm}
          header='Archive this assignment policy?'
          confirmActionText='Archive'
        >
          <p>
            This action is permanent and can’t be undone. Cases will no longer be automatically
            assigned via this policy.
          </p>
        </ConfirmationModal>
      </ModalWrapper>
    </>
  );
};

export default DashboardCaseAssignmentPoliciesView;
